import { useEffect, useState } from 'react';
import { Button } from '@/components/ui';

// Example polygon vertices in the SVG viewBox below — purely illustrative, not map coordinates.
const VIEW_W = 260;
const VIEW_H = 200;
const POINTS: [number, number][] = [
  [55, 45],
  [180, 35],
  [225, 110],
  [150, 170],
  [45, 130],
];
const STEP_MS = 650;

/**
 * A decoupled, illustrative "how to cut turf" animation that floats above the map. It mimics the
 * drawing gesture — dropping vertices one at a time and closing the shape, on a loop — without
 * touching the real Mapbox draw tooling. The overlay is non-blocking so the manager can start
 * drawing for real underneath at any time; the caption is dismissible.
 */
export function TurfDrawDemoOverlay({ onDismiss }: { onDismiss: () => void }) {
  // `revealed` counts placed vertices; one extra step closes the loop, then it resets.
  const [revealed, setRevealed] = useState(1);

  useEffect(() => {
    const id = setInterval(() => {
      setRevealed((n) => (n >= POINTS.length + 1 ? 1 : n + 1));
    }, STEP_MS);
    return () => clearInterval(id);
  }, []);

  const closed = revealed > POINTS.length;
  const placed = POINTS.slice(0, Math.min(revealed, POINTS.length));
  const linePoints = closed ? [...POINTS, POINTS[0]] : placed;
  const cursor = placed[placed.length - 1] ?? POINTS[0];

  return (
    <div className="pointer-events-none fixed inset-0 z-[90] flex items-center justify-center">
      <div className="pointer-events-auto w-[300px] rounded-xl border bg-card p-4 shadow-xl">
        {/* text-primary sets currentColor; shapes inherit it (the app's SVG theming convention). */}
        <svg
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          className="mx-auto h-40 w-full text-primary"
          role="img"
          aria-label="Animation showing how to draw a turf by placing points"
        >
          {/* Filled area once the shape closes */}
          {closed && (
            <polygon
              points={POINTS.map((p) => p.join(',')).join(' ')}
              fill="currentColor"
              fillOpacity={0.15}
              stroke="none"
            />
          )}
          {/* Edges between placed vertices */}
          {linePoints.length > 1 && (
            <polyline
              points={linePoints.map((p) => p.join(',')).join(' ')}
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              strokeDasharray={closed ? undefined : '5 4'}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {/* Placed vertices — solid so they're visible on the white card */}
          {placed.map(([x, y], i) => (
            <circle key={i} cx={x} cy={y} r={5} fill="currentColor" stroke="white" strokeWidth={1.5} />
          ))}
          {/* Pulsing cursor at the most recent vertex */}
          <circle cx={cursor[0]} cy={cursor[1]} r={9} fill="currentColor" fillOpacity={0.3} stroke="none">
            <animate attributeName="r" values="7;11;7" dur="1s" repeatCount="indefinite" />
          </circle>
        </svg>

        <p className="mt-1 text-center text-sm font-medium text-foreground">
          Click points around an area, then close the shape
        </p>
        <p className="mt-1 text-center text-xs text-muted-foreground">
          We&rsquo;ll build a walkable list of the voters inside it.
        </p>

        <div className="mt-3 flex justify-center">
          <Button size="sm" variant="outline" onClick={onDismiss}>
            Got it — let me try
          </Button>
        </div>
      </div>
    </div>
  );
}
